/**
 * Delivery-file policy only: chat attachments and authenticated PM file tools
 * are not covered. This removes download affordances/authorization; browser
 * preview bytes can still be saved or captured and are not copy-protected.
 */
export const SHARE_BROWSER_PREVIEW_MAX_BYTES = 2 * 1024 ** 3;
export const MEDIA_PREVIEW_VERIFICATION_VERSION = 1;

export interface MediaPreviewVerification {
  version: number;
  status: 'compatible' | 'incompatible' | 'unverified';
  reason: string | null;
  container?: string | null;
  videoCodec?: string | null;
  audioCodecs?: string[];
  /** Internal provenance is omitted from public/admin file projections. */
  source?: {
    kind: 'dropbox' | 'r2';
    name: string;
    sizeBytes: number;
    revision?: string;
    key?: string;
    generation?: number;
    etag?: string;
  };
}

export interface DownloadDisabledPreviewFile {
  name?: string;
  fileName?: string;
  mimeType?: string | null;
  size?: number | null;
  sizeBytes?: number | null;
  deliveryMode?: string | null;
  hasPreview?: boolean;
  previewR2Key?: string | null;
  previewStatus?: string | null;
  videoCodec?: string | null;
  audioCodec?: string | null;
  mediaPreviewVerification?: MediaPreviewVerification | null;
  dropboxRevision?: string | null;
  r2Key?: string | null;
  transferAttempts?: number | null;
  transferStatus?: string | null;
}

export interface DownloadPolicyPreflightRequest {
  projectId: string;
  files?: { path: string }[];
  shareId?: number;
  fileIds?: number[];
}

export interface DownloadPolicyPreflightFile {
  path?: string;
  fileId?: number;
  fileName: string;
  compatible: boolean;
  unverified: boolean;
  reason: string | null;
  mediaPreviewVerification: MediaPreviewVerification | null;
}

export interface DownloadPolicyPreflightResponse {
  files: DownloadPolicyPreflightFile[];
  compatible: boolean;
  unverified: boolean;
}

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'm4v', 'webm']);
const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'aac', 'ogg', 'flac', 'm4a']);
const BROWSER_MIMES = new Set([
  'image/jpeg', 'image/pjpeg', 'image/png', 'image/gif', 'image/webp', 'image/avif',
  'video/mp4', 'video/x-m4v', 'video/webm',
  'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/wave', 'audio/vnd.wave',
  'audio/aac', 'audio/ogg', 'audio/vorbis', 'audio/flac', 'audio/x-flac', 'audio/mp4', 'audio/x-m4a',
]);
const VIDEO_CODECS = /^(?:h264|avc1(?:\.[\da-f]+)?|vp8|vp9|vp09(?:\.[\da-f.]+)?|av1|av01(?:\.[\da-z.]+)?)$/i;
const AUDIO_CODECS = /^(?:aac|mp4a(?:\.[\da-f.]+)?|mp3|mp4a\.69|mp4a\.6b|opus|vorbis|flac|pcm(?:_[a-z\d]+)?)$/i;

/**
 * Conservative eligibility aligned with the public page's renderer registry.
 * MIME alone must never make MOV/ProRes/MXF/PDF/archives/unknown files eligible.
 * Pending PSD conversion is not a usable preview; only an existing derivative
 * permits enabling the restriction on PSD/PSB. Thumbnails/group siblings do not.
 * Codec metadata, when present, can disqualify an otherwise supported container.
 */
export function isDownloadDisabledPreviewCandidate(file: DownloadDisabledPreviewFile): boolean {
  const size = file.sizeBytes ?? file.size;
  if (file.deliveryMode === 'dropbox_direct') return false;
  if (size != null && (!Number.isFinite(size) || size <= 0 || size > SHARE_BROWSER_PREVIEW_MAX_BYTES)) return false;

  const name = file.fileName || file.name || '';
  const extension = name.includes('.') ? name.split('.').pop()!.toLowerCase() : '';
  if (extension === 'psd' || extension === 'psb') {
    return !!(file.previewR2Key || file.hasPreview)
      && file.previewStatus !== 'pending' && file.previewStatus !== 'failed';
  }

  const kind = IMAGE_EXTENSIONS.has(extension) ? 'image'
    : VIDEO_EXTENSIONS.has(extension) ? 'video'
    : AUDIO_EXTENSIONS.has(extension) ? 'audio' : null;
  if (!kind) return false;
  const mime = (file.mimeType || '').trim().toLowerCase();
  const baseMime = mime.split(';')[0].trim();
  if (baseMime && baseMime !== 'application/octet-stream'
    && (!BROWSER_MIMES.has(baseMime) || !baseMime.startsWith(`${kind}/`))) return false;
  if (file.videoCodec && !VIDEO_CODECS.test(file.videoCodec)) return false;
  if (file.audioCodec && !AUDIO_CODECS.test(file.audioCodec)) return false;
  const codecs = mime.match(/codecs\s*=\s*"?([^";]+)/)?.[1];
  if (codecs && codecs.split(',').some(codec => {
    const value = codec.trim();
    return kind === 'video'
      ? !VIDEO_CODECS.test(value) && !AUDIO_CODECS.test(value)
      : kind === 'audio' ? !AUDIO_CODECS.test(value) : true;
  })) return false;
  return true;
}

export function requiresMediaPreviewInspection(file: DownloadDisabledPreviewFile): boolean {
  const name = file.fileName || file.name || '';
  const extension = name.split('.').pop()?.toLowerCase() || '';
  return VIDEO_EXTENSIONS.has(extension) || AUDIO_EXTENSIONS.has(extension);
}

export function isDownloadDisabledPreviewCompatible(file: DownloadDisabledPreviewFile): boolean {
  if (!isDownloadDisabledPreviewCandidate(file)) return false;
  if (file.mediaPreviewVerification && file.mediaPreviewVerification.status !== 'compatible') return false;
  if (!requiresMediaPreviewInspection(file)) return true;
  const proof = file.mediaPreviewVerification;
  if (!proof || proof.version !== MEDIA_PREVIEW_VERIFICATION_VERSION || proof.status !== 'compatible') return false;
  // DB/source-bearing records must have provenance. Only whitelisted
  // public/admin projections intentionally omit it.
  if (!proof.source && ('r2Key' in file || 'dropboxRevision' in file || 'transferAttempts' in file)) return false;
  // The server sees provenance; clients see only the server-whitelisted
  // result. A stale source revision or transfer generation never authorizes.
  if (proof.source) {
    const source = proof.source;
    if (source.name !== (file.fileName || file.name) || source.sizeBytes !== (file.sizeBytes ?? file.size)) return false;
    if (source.kind === 'dropbox') return !(file.transferStatus === 'completed' && file.r2Key)
      && !!source.revision && source.revision === file.dropboxRevision;
    return !!source.key && source.key === file.r2Key
      && source.generation === file.transferAttempts && file.transferStatus === 'completed';
  }
  return true;
}

export function publicMediaPreviewVerification(file: DownloadDisabledPreviewFile): MediaPreviewVerification | null {
  const proof = file.mediaPreviewVerification;
  if (!proof) return requiresMediaPreviewInspection(file)
    ? { version: MEDIA_PREVIEW_VERIFICATION_VERSION, status: 'unverified', reason: 'Actual media encoding has not been inspected' }
    : null;
  const publicProof: MediaPreviewVerification = {
    version: proof.version, status: proof.status, reason: proof.reason,
    container: proof.container, videoCodec: proof.videoCodec, audioCodecs: proof.audioCodecs,
  };
  if (proof.status === 'compatible' && !isDownloadDisabledPreviewCompatible(file)) {
    return { ...publicProof, status: 'unverified', reason: 'Media verification no longer matches the current file' };
  }
  return publicProof;
}

export interface ShareDownloadPolicy {
  disableDownloads?: boolean | null;
  parentShareId?: number | null;
}

/** Children inherit LIVE, never a creation-time copy. Missing parent fails closed. */
export function effectiveShareDisableDownloads(
  share: ShareDownloadPolicy,
  parent: ShareDownloadPolicy | null,
): boolean {
  return share.parentShareId != null ? !parent || parent.disableDownloads === true : share.disableDownloads === true;
}

export const DOWNLOADS_DISABLED_ERROR = 'Downloads are disabled for this share';
export const DOWNLOADS_DISABLED_PREVIEW_ERROR =
  'Disable downloads requires a usable browser preview for every delivery file and verified browser-compatible video/audio encoding. Remove unsupported files or inspect their actual encoding first.';